import React, { useEffect, useState } from "react";

import IconButton from "@cayuse/shared-react.components.icon-button";
import { isNonEmptyArray } from "@cayuse/shared-react.utils.type-checks";
import { isEqual } from "lodash";
import { v4 as uuidv4 } from "uuid";

import { withFlowableStatefulFormComponent } from "components/flowable/wrappers/FlowableStatefulFormComponentWrapper";
import useDictionary from "hooks/useDictionary";

import DataTableRow from "./components/DataTableRow";
import styles from "./styles.css";
import { parseResponse, preparePayload } from "./utils/dataTable.utils";

const initialRow = {
	key: uuidv4(),
};

const DataTable = props => {
	const dictionary = useDictionary("dataTable") || {};
	const [ state, setState ] = useState({
		touched: false,
		formValid: true,
		loaded: false,
		rows: undefined,
		columns: [],
	});

	const {
		Components,
		onChange,
		config: {
			enabled,
			value: configValue,
			extraSettings: {
				columns: configColumns = [],
			},
		},
	} = props;

	useEffect(() => {
		// Defensive: only load configValue into state on initial load or if configValue changes from empty to non-empty, to avoid overwriting user input while editing
		if(!state.loaded || (isNonEmptyArray(configValue?.rows) && isEqual(state.rows, [ initialRow ]))) {
			setState(prevState => ({
				...prevState,
				rows: isNonEmptyArray(configValue?.rows) ? parseResponse(configValue) : [ initialRow ],
				columns: configColumns,
				loaded: true,
			}));
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [ configValue ]);

	useEffect(() => {
		if (!state.loaded) {
			return;
		}

		const payload = preparePayload(state.rows, state.columns);
		if(!isEqual(payload, configValue)) {
			onChange(payload);
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [ state.rows ]);

	const addRow = () => {
		setState(prevState => ({
			...prevState,
			rows: [ ...prevState.rows, { key: uuidv4() } ],
		}));
	};

	const removeRow = rowIndex => {
		setState(prevState => ({
			...prevState,
			rows: [ ...prevState.rows ].filter((_, index) => index !== rowIndex),
		}));
	};

	const setCellValue = (value, rowIndex, columnLabel) => {
		const expressionFieldLabel = state.columns.find(column => column.id === "expressionField")?.label;
		const preventUpdate = state.rows.length === 1 &&
			Object.keys(state.rows[0]).length === 1 &&
				expressionFieldLabel &&
					columnLabel === expressionFieldLabel;
		// In the case of an expression field, we want to prevent updating the state if it's the only field in the row and the value is being set for that field, to avoid overwriting the entire row object with just the expression value
		if (preventUpdate) {
			return;
		}

		setState(prevState => ({
			...prevState,
			rows: [ ...prevState.rows ].map((row, index) => index === rowIndex ? { ...row, [columnLabel]: value } : row),
		}));
	};

	const bem = props.bem("panel", { disabled: !enabled });

	if (!state.loaded) {
		return null;
	}

	return (
		<div className={bem()}>
			<div className={styles.label}>
				<Components.label
					{...props}
					config={props.config}
					className={`${
						props.config.isRequired
							? "flw__component--required__label"
							: ""
					}`}
				/>
			</div>
			<Components.description {...props} />
			<div className={styles.dataTableContainer}>
				<div className={styles.dataTableScrollWrapper}>
				<table className={styles.dataTable}>
					{isNonEmptyArray(state.columns) &&
					<thead className={styles.dataTableHeader}>
						<tr>
							{state.columns.map(column => (
								<th key={column.label} className={`${styles.dataTableHeaderCell} ${styles[column.id]}`}>
									<span>{column.label}</span>
									{(column.required || column.component?.required) && <span className={styles.requiredAsterisk}>*</span>}
								</th>
							))}
							<th className={styles.dataTableHeaderCell}></th>
						</tr>
					</thead>
					}
					{isNonEmptyArray(state.rows) && state.rows.map((row, index) => (
						<DataTableRow
							{...props}
							key={row.key}
							rowIndex={index}
							data={row}
							row={row}
							columns={state.columns}
							setCellValue={setCellValue}
							removeRow={removeRow}
						/>
					))}
					<tr>
						<td colSpan={state.columns.length}>
							<IconButton
								icon="plus"
								onClick={addRow}
								labelPosition="left"
								secondary
								className={styles.addRowButton}
								disabled={!enabled}
							>
								{dictionary.addRowBtnLabel ?? "Add Row"}
							</IconButton>
						</td>
					</tr>
				</table>
				</div>
			</div>
		</div>
	);
};

export default withFlowableStatefulFormComponent(DataTable);
